import type { IntlService } from 'ember-intl'

/**
 * @module locales
 * @description contains functions for working with locales.
 *
 * By convention region subtags of a BCP 47 Language tag for are uppercase (eg en-AU), but this does not carry meaning
 * and should be trated as case insensitive. This module handles all comparisons in lowercase.
 * language[-region]
 * https://datatracker.ietf.org/doc/html/rfc5646
 * https://www.techonthenet.com/js/language_tags.php
 *
 * "Locale. An identifier (such as a language tag) for a set of international preferences. Usually this identifier
 * indicates the preferred language of the user and possibly includes other information, such as a geographic region
 * (such as a country)"
 * https://www.w3.org/TR/ltli/#locale
 */

export const DEFAULT_LANGUAGE = 'en-au'

// language tag -> locale settings
export const supportedLanguages = {
  'en-au': {
    intlLocales: ['en-au', 'en'], // primary + fallback locales for translations (all as language tags)
    contentfulLocale: 'en-AU', // Annoyingly, contentful API locales are case-sensitive
    defaultTimezone: 'Australia/Sydney',
  },
  'en-ca': {
    intlLocales: ['en-ca', 'en-us', 'en'],
    contentfulLocale: 'en-CA',
    defaultTimezone: 'Canada/Eastern',
  },
  'en-gb': {
    intlLocales: ['en-gb', 'en'],
    contentfulLocale: 'en-GB',
    defaultTimezone: 'Europe/London',
  },
  'en-us': {
    intlLocales: ['en-us', 'en'],
    contentfulLocale: 'en-US',
    defaultTimezone: 'US/Eastern',
  },
  ko: {
    intlLocales: ['ko', 'en-us', 'en'],
    contentfulLocale: 'ko',
    defaultTimezone: 'Asia/Seoul',
  },
  zh: {
    intlLocales: ['zh', 'en-us', 'en'],
    contentfulLocale: 'zh-CN',
    defaultTimezone: 'Asia/Shanghai',
  },
}

export function isSupportedLanguage(maybeLanguageTag: unknown): maybeLanguageTag is keyof typeof supportedLanguages {
  return Object.keys(supportedLanguages).includes(maybeLanguageTag as keyof typeof supportedLanguages)
}

export function getIntlLocales(languageTag?: string) {
  const lowerLanguageTag = languageTag?.toLowerCase()
  if (isSupportedLanguage(lowerLanguageTag)) {
    return supportedLanguages[lowerLanguageTag].intlLocales
  } else {
    return supportedLanguages[DEFAULT_LANGUAGE].intlLocales
  }
}

export function getContentfulLocale(languageTag?: string) {
  const lowerLanguageTag = languageTag?.toLowerCase()
  if (isSupportedLanguage(lowerLanguageTag)) {
    return supportedLanguages[lowerLanguageTag].contentfulLocale
  } else {
    return supportedLanguages[DEFAULT_LANGUAGE].contentfulLocale
  }
}

export function getDefaultTimezone(languageTag?: string) {
  const lowerLanguageTag = languageTag?.toLowerCase()
  if (isSupportedLanguage(lowerLanguageTag)) {
    return supportedLanguages[lowerLanguageTag].defaultTimezone
  } else {
    return supportedLanguages[DEFAULT_LANGUAGE].defaultTimezone
  }
}

/**
 * Use this to change the language used for translations for the current user
 */
export async function setIntlLanguage(intl: IntlService, languageTag: string) {
  // Based on the given language tag, set the language translation file to use w/ fallbacks, for ember-intl
  const locales = getIntlLocales(languageTag)

  for (const locale of locales) {
    await loadTranslations(intl, locale)
  }

  intl.setLocale(locales)
}

async function loadTranslations(intl: IntlService, language: string) {
  const { default: translations } = await import(`@translations/${language}.json`)
  intl.addTranslations(language, translations)
}
